/**
 * Quantity Selector Component
 *
 * Stepper shown on products that allow a quantity. Clicks are kept off the card's
 * own select/deselect handler, so changing the count never toggles the product away.
 *
 * @package
 */

import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { MAX_QUANTITY, normaliseQuantity } from '../helpers/priceHelpers';

const QuantitySelector = ( { quantity, onChange, productTitle, disabled = false } ) => {
	const units = normaliseQuantity( quantity );

	const step = delta => event => {
		event.stopPropagation();
		onChange( Math.min( Math.max( units + delta, 1 ), MAX_QUANTITY ) );
	};

	// The stepper sits inside a card that is itself a button, so swallow the events
	// that would otherwise bubble up and toggle the selection.
	const swallow = event => event.stopPropagation();

	return (
		<div
			className="configurator-quantity"
			onClick={ swallow }
			onKeyDown={ swallow }
			role="presentation"
		>
			<button
				type="button"
				className="configurator-quantity-button"
				onClick={ step( -1 ) }
				disabled={ disabled || units <= 1 }
				aria-label={ sprintf(
					/* translators: %s: product name */
					__( 'Decrease quantity of %s', 'at-vehicle-configurator' ),
					productTitle
				) }
			>
				<span aria-hidden="true">−</span>
			</button>
			<span className="configurator-quantity-value" aria-live="polite">
				<span aria-hidden="true">{ units }</span>
				<span className="screen-reader-text">
					{ sprintf(
						/* translators: 1: quantity, 2: product name */
						__( 'Quantity: %1$d of %2$s', 'at-vehicle-configurator' ),
						units,
						productTitle
					) }
				</span>
			</span>
			<button
				type="button"
				className="configurator-quantity-button"
				onClick={ step( 1 ) }
				disabled={ disabled || units >= MAX_QUANTITY }
				aria-label={ sprintf(
					/* translators: %s: product name */
					__( 'Increase quantity of %s', 'at-vehicle-configurator' ),
					productTitle
				) }
			>
				<span aria-hidden="true">+</span>
			</button>
		</div>
	);
};

QuantitySelector.propTypes = {
	quantity: PropTypes.number,
	onChange: PropTypes.func.isRequired,
	productTitle: PropTypes.string.isRequired,
	disabled: PropTypes.bool,
};

QuantitySelector.defaultProps = {
	quantity: 1,
	disabled: false,
};

export default QuantitySelector;
